import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { HttpUtils } from '../utils/http-utils';
import { ActivityStatus } from '../domain/activity-status.model';
import { PublisherAdUnitRequestsResponse } from '../domain/publisher-requests-response.model';
import { PublisherAdUnitRequest } from '../domain/publisher-ad-unit-request.model';
import { UnitRequestsRequest } from '../domain/unit-requests-request.model';
import { Publisher } from '../domain/publisher.model';
import { PublisherNote } from '../domain/publisher-note.model';
import { Domain, DomainManagement } from '../domain/domain.model';
import { BridgeAdUnit } from '../domain/bridge-ad-unit.model';
import { PublisherStatus } from '../domain/publisher-status.enum';
import { OnboardingStage } from '../domain/onboarding-stage.enum';
import { Page } from '../domain/page.model';
import { PublisherChangeLog } from '../domain/publisher-change-log.model';
import { ConsumableAdstxtRecord } from '../domain/consumable-adstxt-record.model';
import {PublisherFileUpload} from '../domain/publisher-file-upload.model';


@Injectable()
export class AdStatsPublishersService {

  route = `${environment.serviceUrl}/ad-stats/adu/publisher`;

  constructor(private http: HttpClient) {
  }



  getPublisherAdstxtPerDomain(pub: Publisher): Observable<AdsTxtDiff[]> {
    return this.http.get<AdsTxtDiff[]>(`${this.route}/adstxt/${pub.id}/domains`);
  }

  publisherAdstxt(id: string): Observable<ConsumableAdstxtRecord[]> {
    return this.http.get<ConsumableAdstxtRecord[]>(`${this.route}/${id}/pub-adstxt`);
  }

  publisherAppAdstxt(id: string): Observable<ConsumableAdstxtRecord[]> {
    return this.http.get<ConsumableAdstxtRecord[]>(`${this.route}/${id}/pub-appadstxt`);
  }

  sendPublishersAdstxtReminderEmail(pubs: Publisher[]) {
    return this.http.post(`${environment.serviceUrl}/ad-stats/adu/send-publishers-adtxtxreminder-email`, pubs.map(p => p.id));
  }

  sendAllPublishersAdstxtReminderEmail() {
    return this.http.post(`${environment.serviceUrl}/ad-stats/adu/send-all-publishers-adtxtxreminder-email`, null);
  }

  listAllApprovedPublishers(): Observable<Page<Publisher>> {
    return this.listPublishersByStatus({page: 0, size: 10000, statuses: [PublisherStatus.APPROVED]});
  }

  listAllActivePublishers(): Observable<Page<Publisher>> {
    return this.listPublishers(10000, [ActivityStatus.ACTIVE]);
  }

  listPublishers(size, activityStatuses: ActivityStatus[] = []): Observable<Page<Publisher>> {
    return this.http.get<Page<Publisher>>(`${this.route}`, {
      params: HttpUtils.getHttpParams({
        size: size,
        activityStatuses: activityStatuses ? activityStatuses : []
      })
    });
  }

  listPublishersByStatus(filter: PublisherListRequestFilter): Observable<Page<Publisher>> {
    return this.http.get<Page<Publisher>>(`${this.route}`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  listPublishersWithMissingAdstxt(filter: PublisherListRequestFilter): Observable<Page<Publisher>> {
    return this.http.get<Page<Publisher>>(`${this.route}/adstxt`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  listPublishersCsvExport(filter: PublisherListRequestFilter) {
    return this.http.get(`${this.route}/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(filter)
    });
  }

  searchPublisherByName(name: string, limit = 1000): Observable<Publisher[]> {
    return this.http.get<Publisher[]>(`${this.route}/find-by-name?name=${name}&limit=${limit}`);
  }

  findAllByIdIn(ids: string[]): Observable<Publisher[]> {
    return this.http.get<Publisher[]>(`${this.route}/find-all-by-id-in`, {
      params: HttpUtils.getHttpParams({
        ids: ids
      })
    });
  }

  getPublisherById(id: string): Observable<Publisher> {
    return this.http.get<Publisher>(`${this.route}/${id}`);
  }

  publisherUpdate(pub: Publisher): Observable<Publisher> {
    return this.http.put<Publisher>(`${this.route}`, pub);
  }

  publisherCreate(pub: Publisher): Observable<Publisher> {
    return this.http.post<Publisher>(`${this.route}`, pub);
  }

  publisherRecreateDfpUnit(pub: Publisher): Observable<Publisher> {
    return this.http.post<Publisher>(`${this.route}/${pub.id}/recreate-dfpunit`, null);
  }

  // domains
  createMultipleDomain(pubId: string, request: { urls: string[], management: DomainManagement }): Observable<Domain> {
    return this.http.post<Domain>(`${this.route}/${pubId}/domains`, request);
  }

  createDomain(domain: Domain): Observable<Domain> {
    return this.http.post<Domain>(`${environment.serviceUrl}/ad-stats/adu/domain`, domain);
  }

  updateDomain(domain: Domain) {
    return this.http.put<Domain>(`${environment.serviceUrl}/ad-stats/adu/domain/${domain.id}`, domain);
  }

  importAppCsvFile(file: File, publisherId: string): Observable<PublisherFileUpload> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<PublisherFileUpload>(`${environment.serviceUrl}/ad-stats/adu/publisher/${publisherId}/app-import`, formData);
  }

  getPublisherDomains(id: string): Observable<Domain[]> {
    return this.http.get<Domain[]>(`${this.route}/${id}/domains`);
  }

  getPublisherApprovedDomains(id: string): Observable<Domain[]> {
    return this.http.get<Domain[]>(`${this.route}/${id}/approved-domains`);
  }

  deleteDomain(domain: Domain) {
    return this.http.delete<Domain>(`${environment.serviceUrl}/ad-stats/adu/domain/${domain.id}`);
  }

  getPublisherAdUnits(publisherId: string): Observable<BridgeAdUnit[]> {
    return this.http.get<BridgeAdUnit[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/publisher/${publisherId}/ad-units`);
  }

  getPublishersRequests(params): Observable<PublisherAdUnitRequestsResponse> {
    return this.http.get<PublisherAdUnitRequestsResponse>(`${environment.serviceUrl}/ad-stats/publisher-ad-unit-requests`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  deletePublishersRequest(requestId: string) {
    return this.http.delete(`${environment.serviceUrl}/ad-stats/publisher-ad-unit-requests/${requestId}`);
  }

  rejectPublishersRequest(requestId: string, comment: string): Observable<PublisherAdUnitRequest> {
    return this.http.put<PublisherAdUnitRequest>(`${environment.serviceUrl}/ad-stats/publisher-ad-unit-requests/${requestId}/reject`, comment);
  }

  publishersRequestsExport(filter: UnitRequestsRequest) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/publisher-ad-unit-requests/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(filter)
    });
  }

  publishersRequestsHasRequested(): Observable<number> {
    return this.http.get<number>(`${environment.serviceUrl}/ad-stats/publisher-ad-unit-requests/has-requested`);
  }

  archivePublisher(publisherId: string) {
    return this.http.put(`${this.route}/${publisherId}/archive`, null);
  }

  getPublisherChangeLog(publisherId: string): Observable<{ string: PublisherChangeLog[] }> {
    return this.http.get<{ string: PublisherChangeLog[] }>(`${this.route}/${publisherId}/change-log`);
  }

  // notes
  createPublisherNotes(publisherId: string, note: PublisherNote): Observable<PublisherNote> {
    return this.http.post<PublisherNote>(`${this.route}/${publisherId}/note`, note);
  }

  updatePublisherNotes(note: PublisherNote) {
    return this.http.put(`${this.route}/note/${note.id}`, note);
  }

  getPublisherNotes(publisherId: string): Observable<PublisherNote[]> {
    return this.http.get<PublisherNote[]>(`${this.route}/${publisherId}/note`);
  }

  deletePublisherNotes(noteId: string) {
    return this.http.delete(`${this.route}/note/${noteId}`);
  }

  uploadPublisherNoteFile(file: File, noteId: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post(`${this.route}/note/${noteId}/files`, formData);
  }

  deletePublisherNoteFile(fileName: string, noteId: string) {
    return this.http.delete(`${this.route}/note/${noteId}/files?name=${fileName}`);
  }

}

export class PublisherListRequestFilter {
  name?;
  page?;
  size?;
  tipaltiFinished?: boolean;
  statuses?: PublisherStatus[];
  salesRepresentativeEmail?: string;
  accountManagerEmail?: string;
  activityStatuses?: ActivityStatus[];
  onboardingStages?: OnboardingStage[];
  sortField?: string;
  sortDirection?: string;
}

export class AdsTxtDiff {
  url: string;
  found: ConsumableAdstxtRecord[];
  notFound: ConsumableAdstxtRecord[];
  recentlyAdded: ConsumableAdstxtRecord[];
  recentlyRemoved: ConsumableAdstxtRecord[];
  ext_records: string;
}

export class PublisherOnboardingData {
  publisher: Publisher;
  atLeastOneDomainValidAdsTxt: boolean;
  atLeastOneUnitHasDemandPartners: boolean;
  atLeastOneUnitDraft: boolean;
  atLeastOneUnitReady: boolean;
  atLeastOneSiteApproved: boolean;

}
